'use client';

import { type FC, useState } from 'react';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, ButtonGroup, EmptyState } from '../../components/ui';
import { InviteSheet } from '../../components/InviteSheet';
import { ScreenContent, ScreenTabBar, ScreenFixedBottom, ScreenFixedTop } from '../../screen';
import type { ScreenParams } from '../../types/navigation';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useShareReferral } from '../../hooks/useShareReferral';
import { formatCreditsNumber } from '../../utils/formatters';
import { Credits } from '../../components/Credits';
import { REFERRAL_BASE_REWARD_CREDITS } from './constants';
import { ReferralContestBanner } from '../../components/ReferralContestBanner';
import { ReferralList } from './ReferralList';
import { ReferralsSkeleton } from './ReferralsSkeleton';
import { ReferralStatsBlock } from './ReferralStatsBlock';
import { useReferralsData } from './useReferralsData';
import translations from './ReferralScreen.translations';
import { FaShare } from '../../components/icons';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { DesktopSidebarPanel } from '../../components/desktop/DesktopSidebarPanel';
import './ReferralScreen.css';

interface ReferralScreenProps {
  params?: ScreenParams;
}

export const ReferralScreen: FC<ReferralScreenProps> = () => {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const { referrals, totalCount, stats, loading, loadingMore, error, loadReferrals, loadMore } = useReferralsData();
  const rewardCredits = stats?.rewardCredits ?? REFERRAL_BASE_REWARD_CREDITS;
  const { share, isSharing, referralLink } = useShareReferral({
    messageText: t('shareMessage'),
    buttonText: t('shareButtonText'),
  });

  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);

  const scrollRef = useInfiniteScroll({
    hasMore: referrals.length < totalCount,
    loading: loadingMore,
    onLoadMore: loadMore,
    threshold: 200,
  });

  const handleInviteFriends = async () => {
    await share();
    setInviteSheetOpen(false);
  };

  const inviteButton = (
    <Button
      size={isDesktop ? 's' : 'l'}
      stretched
      variant="cta"
      onClick={() => setInviteSheetOpen(true)}
      before={<FaShare />}
    >
      {t('invite')}
      <span className="ms-2 text-white font-bold text-sm">
        <Credits amount={rewardCredits} />
      </span>
    </Button>
  );

  // WHY: не внутри ScreenFixedBottom — он порталит детей наружу из провайдеров, которые нужны шторке
  const inviteSheet = (
    <InviteSheet
      isOpen={inviteSheetOpen}
      onClose={() => setInviteSheetOpen(false)}
      link={referralLink}
      onShare={handleInviteFriends}
      isSharing={isSharing}
    />
  );

  if (loading) {
    return (
      <>
        <ScreenTabBar />
        <ScreenFixedTop title={t('title')} />
        {inviteSheet}
        <ScreenFixedBottom>
          <ButtonGroup>{inviteButton}</ButtonGroup>
        </ScreenFixedBottom>
        <ScreenContent>
          <ReferralsSkeleton />
        </ScreenContent>
      </>
    );
  }

  if (error && referrals.length === 0) {
    return (
      <>
        <ScreenTabBar />
        <ScreenFixedTop title={t('title')} />
        <ScreenContent>
          <ErrorMessage error={t('errorsLoad')} onRetry={loadReferrals} />
        </ScreenContent>
      </>
    );
  }

  const referralListContent = (
    <>
      {referrals.length === 0 ? (
        <EmptyState message={t('empty')} />
      ) : (
        <>
          <ReferralList referrals={referrals} />
          {loadingMore && (
            <div className="mt-4">
              <ReferralsSkeleton />
            </div>
          )}
        </>
      )}
    </>
  );

  if (isDesktop) {
    return (
      <ScreenContent scrollRef={scrollRef} scrollKey="referrals">
        {inviteSheet}
        <div className="flex gap-6 p-6">

          <DesktopSidebarPanel width={300} className="flex flex-col overflow-hidden">

            <div className="p-5 pb-3 text-center">
              <h2 className="text-xl font-bold text-white m-0">{t('heroTitle')}</h2>
              <p className="text-sm text-white/50 m-0 mt-1">{t('heroSubtitle')}</p>
            </div>


            {stats && stats.totalReferrals > 0 && (
              <div className="px-4 pb-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col items-center py-3 rounded-xl bg-white/[0.04]">
                    <span className="text-[10px] text-white/30 uppercase tracking-wider">{t('statsInvited')}</span>
                    <span className="text-2xl font-bold text-white mt-1">{formatCreditsNumber(stats.totalReferrals)}</span>
                  </div>
                  <div className="flex flex-col items-center py-3 rounded-xl bg-white/[0.04]">
                    <span className="text-[10px] text-white/30 uppercase tracking-wider">{t('statsEarned')}</span>
                    <Credits amount={stats.totalEarned} iconSize={16} className="text-2xl font-bold text-white mt-1" />
                  </div>
                </div>
              </div>
            )}


            {stats && (
              <div className="border-t border-white/[0.04] pt-3">
                <ReferralStatsBlock
                  totalReferrals={stats.totalReferrals}
                  totalEarned={stats.totalEarned}
                  rewardCredits={rewardCredits}
                  showStats={false}
                />
              </div>
            )}


            <div className="p-4 border-t border-white/[0.04]">
              {inviteButton}
            </div>
          </DesktopSidebarPanel>


          <div className="flex-1 min-w-0">
            <div className="mb-4">
              <ReferralContestBanner />
            </div>
            {referralListContent}
          </div>
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenTabBar />
      <ScreenFixedTop title={t('title')} />
      {inviteSheet}
      <ScreenFixedBottom>
        <ButtonGroup>
          {inviteButton}
        </ButtonGroup>
      </ScreenFixedBottom>
      <ScreenContent scrollRef={scrollRef} scrollKey="referrals">
        <div className="text-center px-4 pt-6 pb-4">
          <h1 className="text-3xl font-bold text-white m-0 mb-1">{t('heroTitle')}</h1>
          <p className="text-lg font-normal text-white/70 m-0">{t('heroSubtitle')}</p>
        </div>

        <div className="px-4 pb-4">
          <ReferralContestBanner />
        </div>

        {stats && (
          <ReferralStatsBlock
            totalReferrals={stats.totalReferrals}
            totalEarned={stats.totalEarned}
            rewardCredits={rewardCredits}
          />
        )}

        {referralListContent}
      </ScreenContent>
    </>
  );
};
